import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import Link from 'flarum/common/components/Link';

import { currentPath, seesN8nTag, N8N_TAG_SLUG, MEETUPS_TAG_SLUG } from './utils/routeState';

/**
 * (a) Nav del header — Inicio · Discusiones · Recursos · Encuentros · n8n Chile.
 *
 * Spec §4.1 (componentes: header claro sticky con nav), §11 (vocabulario «Encuentros»;
 * «n8n Chile» visible SIEMPRE, anonimo o logueado) y §12 («Recursos» es item de primer nivel
 * en TODOS los frames, entre Discusiones y Encuentros). Orden y copy del mockup D1/D2/D7/D9.
 *
 * EL DRAWER MOVIL ES ESTA MISMA LISTA. Flarum no tiene dos navs: en <768 el core mueve
 * `HeaderPrimary` al drawer del boton ☰, y §11 de forum.less lo apila en vertical con
 * `min-height:44px` por item. Por eso «Recursos» aparece en el drawer sin tocar nada mas —
 * y por eso un nav distinto para movil seria una segunda fuente de verdad que se
 * desincronizaria.
 *
 * `HeaderPrimary.items` viene vacia en el core 1.8 (el buscador y el avatar viven en
 * HeaderSecondary), asi que aqui no se pisa nada. Se agrega un unico item con la lista
 * completa: el core envuelve cada item en `li.item-<key>` y con un solo item el nav
 * mantiene su propio layout sin depender de como el core espacie hermanos.
 */
export default function addHeaderNav() {
  extend(HeaderPrimary.prototype, 'items', function (items: any) {
    const path = currentPath();
    const isTagPath = path.indexOf('/t/') === 0;
    const meetupsPath = app.route('tag', { tags: MEETUPS_TAG_SLUG });
    const encuentrosPath = app.route('umbral-comunidad.encuentros');
    const n8nTagPath = app.route('tag', { tags: N8N_TAG_SLUG });

    // Quien ve la etiqueta restringida aterriza directo en la categoria (D2b); el resto va
    // a la pagina publica del grupo (D7). Asi el enlace nunca lleva a un 404, que es el
    // bug vivo que este paquete cierra.
    const n8nHref = seesN8nTag() ? n8nTagPath : app.route('umbral-comunidad.n8nChile');

    const links = [
      {
        key: 'home',
        href: app.route('index'),
        label: app.translator.trans('umbral-comunidad.forum.nav.home'),
        active: path === '/',
        className: '',
      },
      {
        key: 'discussions',
        href: app.route('umbral-comunidad.discussions'),
        label: app.translator.trans('umbral-comunidad.forum.nav.discussions'),
        // Cualquier feed de categoria sigue siendo «Discusiones», salvo los dos que tienen
        // entrada propia en el nav.
        active:
          path === app.route('umbral-comunidad.discussions') ||
          path === '/tags' ||
          (isTagPath && path !== meetupsPath && path !== n8nTagPath),
        className: '',
      },
      {
        key: 'resources',
        href: app.route('umbral-comunidad.recursos'),
        label: app.translator.trans('umbral-comunidad.forum.nav.resources'),
        active: path === app.route('umbral-comunidad.recursos'),
        className: '',
      },
      {
        key: 'meetups',
        // PKG-ENCUENTROS-GALERIA: el nav apunta ahora a `/encuentros` —el archivo publico con
        // las fichas D6— y ya no a la categoria. Motivo: «Encuentros» en el nav promete lo que
        // ha pasado y lo que viene, y `/t/encuentros` es un feed de hilos con «Nueva discusion»
        // arriba, del que un anonimo puede salir sin ver una sola foto. La categoria NO se
        // esconde: la pagina enlaza a ella en su seccion de propuestas, y el item sigue
        // marcandose activo cuando se esta dentro.
        href: encuentrosPath,
        label: app.translator.trans('umbral-comunidad.forum.nav.meetups'),
        active: path === encuentrosPath || path.indexOf(encuentrosPath + '/') === 0 || path === meetupsPath,
        className: '',
      },
      {
        key: 'n8n',
        href: n8nHref,
        label: app.translator.trans('umbral-comunidad.forum.nav.n8n'),
        active: path === n8nTagPath || path === app.route('umbral-comunidad.n8nChile'),
        className: 'cm-nav-link--n8n',
      },
    ];

    items.add(
      'umbralNav',
      <ul className="cm-nav" data-cm-nav="1">
        {links.map((link) => (
          <li className={'cm-nav-item cm-nav-item--' + link.key}>
            <Link
              href={link.href}
              className={'cm-nav-link ' + link.className + (link.active ? ' active' : '')}
              aria-current={link.active ? 'page' : undefined}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>,
      100
    );
  });
}
